import type { BusinessSegmentConditions, SegmentConditionGroups } from '@reach/shared-types/conditions';
import { Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { SearchableSelect } from '../../../components/ui/searchable-select';
import {
  isLegacySegmentFormat,
  type PreviewRecipient,
} from '../engage-segments-tab/segment-definitions-api';
import { usePreviewSegmentRecipients, useTenants } from '../engage-segments-tab/useSegmentTemplates';
import styles from './EditSegment.module.css';
import { RecipientCard } from './RecipientCard';

interface RecipientsPreviewProps {
  conditions?: BusinessSegmentConditions | null;
  conditionGroups?: SegmentConditionGroups | null;
  isJsonValid: boolean;
}

export const RecipientsPreview: React.FC<RecipientsPreviewProps> = ({
  conditions,
  conditionGroups,
  isJsonValid,
}) => {
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [recipients, setRecipients] = useState<PreviewRecipient[]>([]);
  const [total, setTotal] = useState<number | null>(null);

  const { data: tenants = [], isLoading: isLoadingTenants } = useTenants();
  const { mutate: previewRecipients, isPending: isValidating } = usePreviewSegmentRecipients();

  const tenantOptions = useMemo(
    () => tenants.map(t => ({ id: t.id, label: t.name || t.id })),
    [tenants],
  );

  const handleTenantChange = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setRecipients([]);
    setTotal(null);
  };

  const isLegacyFormat = isLegacySegmentFormat({ conditions, conditionGroups });
  const hasPreviewPayload = isLegacyFormat
    ? (conditions?.length ?? 0) > 0
    : (conditionGroups?.length ?? 0) > 0;

  const handleValidate = () => {
    if (!selectedTenantId || !hasPreviewPayload || !isJsonValid) return;

    if (isLegacyFormat) {
      // Legacy format: use conditions (like main branch)
      previewRecipients(
        {
          tenantId: selectedTenantId,
          conditions: conditions ?? undefined,
          limit: 20,
        },
        {
          onSuccess: data => {
            setRecipients(data.recipients);
            setTotal(data.estimatedTotal);
          },
        },
      );
    } else {
      // New format: use conditionGroups
      previewRecipients(
        {
          tenantId: selectedTenantId,
          conditionGroups: conditionGroups ?? undefined,
          limit: 20,
        },
        {
          onSuccess: data => {
            setRecipients(data.recipients);
            setTotal(data.estimatedTotal);
          },
        },
      );
    }
  };

  const canValidate = selectedTenantId && hasPreviewPayload && isJsonValid;

  return (
    <aside className={styles.preview}>
      <header>
        <h3>Recipients Preview</h3>
      </header>

      <section className={styles.controls}>
        <div>
          <label htmlFor="tenant-select">Validate Against Tenant</label>
          <SearchableSelect
            options={tenantOptions}
            value={selectedTenantId}
            onChange={handleTenantChange}
            placeholder="Select a tenant..."
            searchPlaceholder="Search tenants..."
            isLoading={isLoadingTenants}
            loadingText="Loading tenants..."
            emptyText="No tenants found"
          />
        </div>

        <Button onClick={handleValidate} disabled={!canValidate || isValidating}>
          {isValidating ? 'Validating...' : 'Validate Segment'}
        </Button>
      </section>

      <section className={styles.results}>
        <RecipientsResults recipients={recipients} total={total} />
      </section>
    </aside>
  );
};

interface RecipientsResultsProps {
  recipients: PreviewRecipient[];
  total: number | null;
}

function RecipientsResults({ recipients, total }: RecipientsResultsProps): React.ReactElement {
  if (total === null) {
    return (
      <figure className={styles.emptyState}>
        <Search />
        <p>Select a tenant and click validate to preview recipients</p>
      </figure>
    );
  }

  if (recipients.length === 0) {
    return <p className={styles.noResults}>No matching recipients found</p>;
  }

  return (
    <>
      <p className={styles.resultsSummary}>
        <strong>{total}</strong> matching recipient{total !== 1 ? 's' : ''}
      </p>
      <ul className={styles.recipientList}>
        {recipients.map(recipient => (
          <RecipientCard key={recipient.id} recipient={recipient} />
        ))}
      </ul>
      {total > recipients.length && (
        <p className={styles.showingCount}>
          Showing {recipients.length} of {total}
        </p>
      )}
    </>
  );
}
